import React from 'react';
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import css from './CompetitorPage.module.css';

const COMPARISON_ROWS = [
  { feature: 'Porsche-only?', rennlist: 'Forum-wide (mixed)', rennzone: '✓ 100% Porsche' },
  { feature: 'Dedicated marketplace', rennlist: '✗ Forum section only', rennzone: '✓ Purpose-built' },
  { feature: 'Cars + Parts + Memorabilia', rennlist: 'Partial', rennzone: '✓ All three' },
  { feature: 'Global reach', rennlist: 'Mostly US', rennzone: '✓ Global' },
  { feature: 'Mobile optimized', rennlist: '✗', rennzone: '✓' },
  { feature: 'Seller profiles', rennlist: '✗', rennzone: '✓' },
  { feature: 'Easy to list', rennlist: 'Multiple steps', rennzone: '✓ Quick listing' },
  { feature: 'Free to browse', rennlist: '✓', rennzone: '✓' },
];

const FAQ_ITEMS = [
  {
    q: 'Is Rennzone the same as Rennlist?',
    a: 'No. Rennzone is an independent Porsche marketplace, not affiliated with Rennlist. Rennlist is an enthusiast forum; Rennzone is a purpose-built buying and selling platform for Porsche cars, parts, and memorabilia.',
  },
  {
    q: 'Can I list a Porsche for sale on Rennzone?',
    a: 'Yes. Create a free account, add your listing with photos and description, and your Porsche reaches a global audience of buyers immediately.',
  },
  {
    q: 'Does Rennzone list Porsche parts as well as cars?',
    a: 'Yes — Rennzone covers Porsche cars, parts, books, memorabilia, and race cars. All categories are Porsche-specific.',
  },
];

const CompetitorPage = props => {
  const { scrollingDisabled } = props;

  const faqSchema = {
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <Page
      title="Rennlist Classifieds Alternative — Buy & Sell Porsche on Rennzone"
      description="Looking for Rennlist classifieds? Rennzone is the dedicated Porsche-only marketplace. Easier to list, global reach, Porsche cars, parts & memorabilia."
      schema={faqSchema}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
        mainColumnClassName={css.main}
      >
        <div className={css.root}>

          {/* Breadcrumb */}
          <div className={css.breadcrumb}>
            <div className={css.breadcrumbInner}>
              <a href="/" className={css.breadcrumbLink}>Home</a>
              <span className={css.breadcrumbSep}>›</span>
              <span className={css.breadcrumbCurrent}>Rennlist Alternative</span>
            </div>
          </div>

          {/* Hero */}
          <div className={css.hero}>
            <div className={css.heroInner}>
              <span className={css.heroLabel}>Rennlist Alternative</span>
              <h1 className={css.h1}>Looking for Rennlist Classifieds? Try Rennzone.</h1>
              <p className={css.heroSubtitle}>
                A dedicated Porsche-only marketplace — purpose-built for buying and selling
                Porsche cars, parts, memorabilia, and race cars globally.
              </p>
              <div className={css.heroCtaRow}>
                <a href="/s" className={css.ctaPrimary}>Browse Listings</a>
                <a href="/l/new" className={css.ctaSecondary}>List Your Porsche</a>
              </div>
            </div>
          </div>

          {/* Body — two column */}
          <div className={css.body}>
            <div className={css.bodyInner}>

              {/* Left — content */}
              <div className={css.content}>

                <h2 className={css.h2}>What Is Rennlist?</h2>
                <p className={css.p}>
                  Rennlist.com is the world's largest Porsche enthusiast forum, founded in 1996.
                  Its classifieds section has historically been one of the most popular places to
                  buy and sell Porsche cars and parts within the community. For decades it served
                  the Porsche world well.
                </p>

                <h2 className={css.h2}>The Problem With Forum Classifieds in 2026</h2>
                <p className={css.p}>Forum classifieds worked well in 2005. In 2026, the limitations are real:</p>
                <ul className={css.list}>
                  <li>Listings buried in threads — no model, price, or location filters</li>
                  <li>No dedicated seller or buyer profiles, no reputation system</li>
                  <li>No standardised listing format — photos and specs vary wildly</li>
                  <li>Regional bias — predominantly North American listings</li>
                  <li>No mobile-optimised buying experience</li>
                  <li>No dedicated parts or memorabilia category</li>
                </ul>

                <h2 className={css.h2}>How Rennzone Is Different</h2>
                <div className={css.tableWrap}>
                  <table className={css.table}>
                    <thead className={css.thead}>
                      <tr>
                        <th className={css.th}>Feature</th>
                        <th className={css.th}>Rennlist Classifieds</th>
                        <th className={`${css.th} ${css.thRennzone}`}>Rennzone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {COMPARISON_ROWS.map(row => (
                        <tr key={row.feature} className={css.tr}>
                          <td className={css.tdFeature}>{row.feature}</td>
                          <td className={css.td}>{row.rennlist}</td>
                          <td className={`${css.td} ${css.tdRennzone}`}>{row.rennzone}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h2 className={css.h2}>What Rennzone Lists That Rennlist Doesn't Cover Well</h2>
                <ul className={css.list}>
                  <li>Porsche books, factory literature, and workshop manuals</li>
                  <li>Signed racing memorabilia and factory driver items</li>
                  <li>Porsche-branded watches and clothing</li>
                  <li>International listings — UK, Europe, Middle East, and Asia-Pacific</li>
                  <li>Race cars and Cup cars — dedicated motorsport section</li>
                </ul>

                <h2 className={css.h2}>How to List on Rennzone</h2>
                <p className={css.p}>Listing is straightforward:</p>
                <ol className={css.list}>
                  <li><strong>Create a free account</strong> — takes under two minutes</li>
                  <li><strong>Add your listing</strong> — photos, description, price, model details</li>
                  <li><strong>Go live</strong> — your listing reaches a global Porsche audience immediately</li>
                </ol>
                <p className={css.p}>
                  No forum rules, no thread management. A clean listing that buyers can filter
                  by model, year, price, and location.
                </p>

                <div className={css.ctaRow}>
                  <a href="/s" className={css.ctaPrimary}>Browse Listings</a>
                  <a href="/l/new" className={css.ctaSecondary}>List Your Porsche</a>
                </div>

                <h2 className={css.h2}>Frequently Asked Questions</h2>
                <dl className={css.faqList}>
                  {FAQ_ITEMS.map((item, i) => (
                    <div key={i} className={css.faqItem}>
                      <dt className={css.faqQ}>{item.q}</dt>
                      <dd className={css.faqA}>{item.a}</dd>
                    </div>
                  ))}
                </dl>

                <h2 className={css.h2}>Buying Guides & Market Insight</h2>
                <p className={css.p}>
                  Rennzone is more than a marketplace. Read independent buyer's
                  guides and market analysis written by Porsche specialists:
                </p>
                <ul className={css.list}>
                  <li><a href="/blog" className={css.seeAlsoLink}>Browse all Porsche buyer's guides</a></li>
                  <li><a href="/blog" className={css.seeAlsoLink}>Market reports &amp; collectability analysis</a></li>
                  <li><a href="/blog" className={css.seeAlsoLink}>Ownership &amp; storage advice</a></li>
                </ul>

                <div className={css.seeAlso}>
                  <span className={css.seeAlsoLabel}>See also:</span>
                  <a href="/porsche-classifieds" className={css.seeAlsoLink}>Porsche Classifieds</a>
                  <span className={css.seeAlsoSep}>|</span>
                  <a href="/parts" className={css.seeAlsoLink}>Porsche Parts</a>
                  <span className={css.seeAlsoSep}>|</span>
                  <a href="/cars/race-cars" className={css.seeAlsoLink}>Race Cars</a>
                  <span className={css.seeAlsoSep}>|</span>
                  <a href="/blog" className={css.seeAlsoLink}>Blog</a>
                </div>
              </div>

              {/* Right — sidebar */}
              <div className={css.sidebar}>
                <div className={css.sidebarCard}>
                  <p className={css.sidebarHeading}>Start on Rennzone</p>
                  <a href="/s" className={css.sidebarCtaPrimary}>Browse All Listings</a>
                  <a href="/l/new" className={css.sidebarCtaSecondary}>List Your Porsche</a>
                  <hr className={css.sidebarDivider} />
                  <p className={css.sidebarHeading}>Browse by Category</p>
                  <ul className={css.sidebarLinkList}>
                    <li><a href="/s" className={css.sidebarLink}>All Cars →</a></li>
                    <li><a href="/cars/race-cars" className={css.sidebarLink}>Race Cars →</a></li>
                    <li><a href="/cars/air-cooled" className={css.sidebarLink}>Air-Cooled →</a></li>
                    <li><a href="/parts" className={css.sidebarLink}>Parts →</a></li>
                    <li><a href="/memorabilia" className={css.sidebarLink}>Memorabilia & Books →</a></li>
                  </ul>
                  <hr className={css.sidebarDivider} />
                  <p className={css.sidebarHeading}>Read the Blog</p>
                  <a href="/blog" className={css.sidebarCtaSecondary}>Porsche Buyer's Guides →</a>
                </div>
              </div>

            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default CompetitorPage;
